import React, { useMemo, useRef, useEffect, useState } from 'react';
import JSCookie from 'js-cookie';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { TRACK_CLICK_READ_REVIEWS } from '../../../../../src/web/utils/dataLayer';
import EditableRichText from '../../../../../src/web/containers/EditableRichText';
import StorageMartHours from '../../../components/stateless/StorageMartHours';
import Translation from '../../../../components/stateless/Translation';
import reviewStars from './four-and-a-half-stars.svg';
import Price from '../../../../components/stateless/Price';
import styles from './styles.scss';
import useMedia from '../../../../../modules/react/hooks/useMedia';
import { UK_FORM_COOKIE } from '../../../../../modules/storagemart';
import { EN_GB } from '../../../../../modules/i18n/locales';


const propTypes = {
  facility: PropTypes.shape({
    name: PropTypes.string.isRequired,
    address: PropTypes.shape({
      address1: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      postal: PropTypes.string.isRequired,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }).isRequired,
    primaryImage: PropTypes.string,
    unitGroups: PropTypes.object,
    unitSummary: PropTypes.shape({
      minPriceWeekly: PropTypes.number,
      minPrice: PropTypes.number,
    }),
    currency: PropTypes.string,
    currencyLocale: PropTypes.string,
    settings: PropTypes.shape({
      useWeeklyRates: PropTypes.bool.isRequired,
    }).isRequired,
  }),
  description: PropTypes.shape({}),
  header: PropTypes.shape({}),
  theme: PropTypes.shape({
    fonts: PropTypes.shape({
      primary: PropTypes.shape({
        name: PropTypes.string,
        href: PropTypes.string,
      }),
      secondary: PropTypes.shape({
        name: PropTypes.string,
        href: PropTypes.string,
      }),
    }),
  }),
};

const defaultProps = {
  facility: undefined,
  description: undefined,
  header: undefined,
  theme: undefined,
};


const getBackgroundImageUrl = (latitude, longitude) => {
  let size;
  const deviceType = useMedia(
    ['(min-width: 400px)', '(min-width: 600px)'],
    ['tablet', 'desktop'],
    'default',
  );
  const isDesktop = deviceType === 'desktop';
  const isTablet = deviceType === 'tablet';

  if (isDesktop) {
    size = '343x200';
  } else if (isTablet) {
    size = '515x200';
  } else {
    size = '343x200';
  }

  return `url('https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=12&size=${size}&markers=${latitude},${longitude}&key=AIzaSyBHzXnAMJ82L6VY9k4XNy-coHC2IyvDCCo')`;
};

const SMFacilityInformation = ({
  facility,
  description,
  header,
}) => {
  const intl = useIntl();
  const { locale } = intl;

  const isUK = locale === EN_GB;
  const foundUKForm = !!JSCookie.get(UK_FORM_COOKIE);
  const isUKFormSubmitted = isUK && foundUKForm;

  if (!facility) {
    return null;
  }

  const deviceType = useMedia(
    ['(max-width: 768px)', '(min-width: 768px)'],
    ['mobile', 'desktop'],
    'default',
  );

  const isMobile = deviceType === 'mobile';

  const [isFixed, setIsFixed] = useState(false);


  const handleScrollToElement = (event, selector) => {
    event.preventDefault();
    const targetElement = document.querySelector(selector);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  useEffect(() => {
    const banner = document.querySelector('#bannerFixed');
    const navigation = document.querySelector('[data-image-test-var-left]');

    if (!banner || !navigation) {
      return;
    }

    const handleScroll = () => {
      const navigationRect = navigation.getBoundingClientRect();
      const isOutOfView = navigationRect.bottom <= 0;

      if (window.innerWidth <= 768) {
        const isInView = navigationRect.top < window.innerHeight && navigationRect.bottom >= 0;
        setIsFixed(!isInView);
      } else {
        setIsFixed(isOutOfView);
      }
    };

    const handleResize = () => {
      if (isMobile) {
        setIsFixed(false);
      } else {
        handleScroll();
      }
    };

    const debouncedHandleScroll = debounce(handleScroll, 100);
    const debouncedHandleResize = debounce(handleResize, 100);

    window.addEventListener('scroll', debouncedHandleScroll);
    window.addEventListener('resize', debouncedHandleResize);

    handleResize();
    handleScroll();

    // eslint-disable-next-line consistent-return
    return () => {
      // eslint-disable-next-line no-restricted-globals
      window.removeEventListener('scroll', debouncedHandleScroll);
      // eslint-disable-next-line no-restricted-globals
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  let isBannerFixed = '';
  let showBanner = '';
  if (isFixed) {
    isBannerFixed = `fixed top-0 left-0 right-0 ${styles.isBannerFixed}`;
    showBanner = 'border-t md:border-b md:px-0';
  } else {
    showBanner = 'border-b mb-4';
  }

  const hasAddress2 = facility.address.address2 ? `${facility.address.address2}, ` : '';
  const googleFriendlyAddress1 = useMemo(() => facility.address.address1.replace(/#/g, ''), [facility.address.address1]);
  const googleMapLink = useMemo(() => `https://www.google.com/maps/dir//${facility.brandName} ${googleFriendlyAddress1}, ${hasAddress2}${facility.address.city}, ${facility.address.state}, ${facility.address.postal}'?hl=${locale}`, [facility.brandName, googleFriendlyAddress1, hasAddress2, facility.address.city, facility.address.state, facility.address.postal]);
  const bgImageUrl = getBackgroundImageUrl(facility.address.latitude, facility.address.longitude);

  const initialSummary = {
    minPrice: Infinity,
    minPriceWeekly: Infinity,
  };

  const cheapestUnitSummary = facility.unitSummary.reduce((cheapest, current) => ({
    minPrice: current.minPrice < cheapest.minPrice ? current.minPrice : cheapest.minPrice,
    minPriceWeekly: current.minPriceWeekly < cheapest.minPriceWeekly
      ? current.minPriceWeekly : cheapest.minPriceWeekly,
  }), initialSummary);

  const isPricingAvailable = facility.settings.useWeeklyRates
    // eslint-disable-next-line no-restricted-globals
    ? (cheapestUnitSummary.minPriceWeekly !== null && !isNaN(cheapestUnitSummary.minPriceWeekly))
    // eslint-disable-next-line no-restricted-globals
    : (cheapestUnitSummary.minPrice !== null && !isNaN(cheapestUnitSummary.minPrice));

  const showPrice = (isPricingAvailable && !isUK) || (isPricingAvailable && isUKFormSubmitted);


  const [activeLink, setActiveLink] = useState('[data-image-test-var-left]');
  const [hasReviews, setHasReviews] = useState(false);
  const [activeWidth, setActiveWidth] = useState('94.625px');
  const [translateX, setTranslateX] = useState('0px');
  const overviewRef = useRef(null);
  const unitstableRef = useRef(null);
  const ctaContainerRef = useRef(null);
  const navRef = useRef(null);
  const reviewsRef = useRef(null);

  const updateActiveLink = (selector, ref) => {
    setActiveLink(selector);
    if (ref.current && navRef.current) {
      const navOffsetLeft = navRef.current.getBoundingClientRect().left;
      const linkOffsetLeft = ref.current.getBoundingClientRect().left;
      const newTranslateX = `${linkOffsetLeft - navOffsetLeft}px`;
      setTranslateX(newTranslateX);
      const newWidth = `${ref.current.offsetWidth}px`;
      setActiveWidth(newWidth);
    }
  };

  const handleLinkClick = (event, selector, ref) => {
    event.preventDefault();
    updateActiveLink(selector, ref);
    const targetElement = document.querySelector(selector);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' });
    }
  };


  useEffect(() => {
    updateActiveLink('[data-image-test-var-left]', overviewRef);
    const reviewsElement = document.getElementById('reviews');
    if (reviewsElement) {
      setHasReviews(true);
    }
  }, [isMobile]);

  return (
    <>
      {facility.unitGroups.length > 0 && (
        <div className="hidden" data-sticky-test id="overview">
          <div
            className={`flex z-50 items-center bg-white backdrop-blur-sm ${isBannerFixed} ${styles.bannerContainer}`}
            id="bannerFixed"
          >
            <div className={`${styles.bannerFixed} w-full mx-auto ${isFixed ? 'my-0 md:px-4' : ''}`}>
              <div className={`flex gap-4 items-center justify-between ${showBanner}`}>
                <nav className={`flex gap-4 md:gap-16 items-center relative ${styles.highlighter}`} ref={navRef} id="navigation">
                  <ul>
                    <li className="relative">
                      <a
                        href="#overview"
                        ref={overviewRef}
                        className={`text-sm text-light-text text-bold ${activeLink === '[data-image-test-var-left]' ? `${styles.active}` : ''}`}
                        onClick={event => handleLinkClick(event, '[data-image-test-var-left]', overviewRef)}
                      >
                        Overview
                      </a>
                    </li>
                    <li>
                      <a
                        href="#unitstable"
                        ref={unitstableRef}
                        className={`text-sm text-light-text ${activeLink === '#unitstable' ? `${styles.active}` : ''}`}
                        onClick={event => handleLinkClick(event, '#unitstable', unitstableRef)}
                      >
                        Units & Prices
                      </a>
                    </li>
                    {hasReviews && (
                      <li>
                        <a
                          href="#reviews"
                          ref={reviewsRef}
                          className={`text-sm text-light-text ${activeLink === '#reviews' ? `${styles.active}` : ''}`}
                          onClick={event => handleLinkClick(event, '#reviews', reviewsRef)}
                        >
                          Reviews
                        </a>
                      </li>

                    )}
                    <li>
                      <a
                        href="#ctaContainer"
                        ref={ctaContainerRef}
                        className={`text-sm text-light-text ${activeLink === '#ctaContainer' ? `${styles.active}` : ''}`}
                        onClick={event => handleLinkClick(event, `${isMobile ? '#ctaCon' : '#ctaContainer'}`, ctaContainerRef)}
                      >
                        Info & FAQ&apos;s
                      </a>
                    </li>
                    <li
                      className={`${styles.liter}`}
                      aria-hidden="true"
                      style={{
                        transform: `translateX(${translateX}) scaleY(1) scaleX(1)`,
                        width: activeWidth,
                      }}
                    />
                  </ul>
                </nav>
                <div className={`flex gap-4 items-center justify-between md:justify-end ${styles.seePrice}`}>
                  <>
                    <div className="grow-0 w-full">
                      {showPrice && (
                        <div data-starting-price>
                          <div className="text-sm text-light-text">
                            <Translation id="enterprise.widgets.nap.Starting" defaultMessage="Starting from " />
                          </div>
                          <span className="font-bold">
                            <Price
                              currency={facility.currency}
                              locale={facility.currencyLocale}
                              value={facility.settings.useWeeklyRates
                                ? cheapestUnitSummary.minPriceWeekly
                                : cheapestUnitSummary.minPrice}
                            />
                            <span>
                              {facility.settings.useWeeklyRates
                                ? <Translation id="widgets.abbreviation.wk" defaultMessage="/wk" />
                                : <Translation id="widgets.abbreviation.mo" defaultMessage="/mo" />
                              }
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                    <a href="#unitstable" className="rnl-Button themed-button max-w-[190px] md:max-w-[220px] w-full" onClick={event => handleScrollToElement(event, '#unitstable')}>
                      <Translation id="enterprise.widgets.nap.seeAvailableUnits" defaultMessage="See Available Units" />
                    </a>
                  </>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={`${styles.root} ${styles.infoContainer}`} data-info-text-container>
        <div className={`${styles.infoContainerLeft}`}>
          <div className={`${styles.headerText} mb-3 mt-0`}>
            <EditableRichText editorKey="sm-facility-info" rawContentState={header} />
          </div>
          <div className={styles.directionsContainer}>
            <a className={`${styles.link} ${styles.cellContainer}`} href="#readreviews" {...TRACK_CLICK_READ_REVIEWS}>
              <img src={reviewStars} type="image/svg+xml" className={styles.starsIcon} alt="stars" loading="eager" height="50" width="292" />
              <span className={styles.link}><Translation id="enterprise.widgets.nap.readReviews" defaultMessage="Read Reviews" /></span>
            </a>
          </div>
          <div className={styles.description}>
            <EditableRichText editorKey="sm-facility-info" rawContentState={description} />
          </div>
        </div>
        <div className={`${styles.infoContainerRight}`}>
          <div className={styles.generalInfo} data-test-old-see-all>
            {(facility.unitGroups.length > 0) && (
              <div className={styles.generalInfoHeading}>
                <a href="#unitstable" className="rnl-Button themed-button">
                  <Translation id="enterprise.widgets.nap.seeAvailableUnits" defaultMessage="See Available Units" />
                </a>
              </div>
            )}
          </div>
          <div className="border border-custom-gray rounded-md mb-4">

            <a href={googleMapLink} target="_blank" rel="noopener noreferrer" className="directionClick block border-b border-custom-gray border-b-[1px] overflow-hidden relative w-full min-h-[110px]">
              <div
                className="absolute inset-0 w-full h-full object-cover bg-cover bg-center"
                style={{
                  backgroundImage: bgImageUrl,
                }}
                data-mapImageTest
              />
              <div className="sr-only">view google directions</div>
            </a>
            <div className="w-full">
              <a href={googleMapLink} target="_blank" rel="noopener noreferrer" className={`directionClick ${styles.cellContainer} flex gap-2 py-4   px-[.75rem] justify-between items-center border-b border-custom-gray hover:bg-gray-100 hover:no-underline`}>
                <div className="flex gap-2 items-start justify-between w-full">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="20" height="20" role="presentation">
                    <path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
                  </svg>
                  <div className="w-full text-left text-black">
                    <h2 className={`truncate hover:text-clip ${styles.addressContainerNew}`}>
                      {facility.address.address1}
                    </h2>
                    <div className="text-gray-600 text-sm truncate hover:text-clip">
                      {facility.address.address2}
                    </div>
                    <div className="text-gray-600 text-sm truncate hover:text-clip">
                      {facility.address.city}, {facility.address.state} {facility.address.postal}
                    </div>
                  </div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="20" height="20" role="presentation">
                  <path d="M227.7 11.7c15.6-15.6 40.9-15.6 56.6 0l216 216c15.6 15.6 15.6 40.9 0 56.6l-216 216c-15.6 15.6-40.9 15.6-56.6 0l-216-216c-15.6-15.6-15.6-40.9 0-56.6l216-216zm87.6 137c-4.6-4.6-11.5-5.9-17.4-3.5s-9.9 8.3-9.9 14.8l0 56-64 0c-35.3 0-64 28.7-64 64l0 48c0 13.3 10.7 24 24 24s24-10.7 24-24l0-48c0-8.8 7.2-16 16-16l64 0 0 56c0 6.5 3.9 12.3 9.9 14.8s12.9 1.1 17.4-3.5l80-80c6.2-6.2 6.2-16.4 0-22.6l-80-80z" />
                </svg>
              </a>
              <StorageMartHours facility={facility} />
              <Translation
                id="sections.enterprise.storageMartHeader.phoneNumber"
                defaultMessage="1234567890"
              >
                {phoneNumber => (
                  <a className="phoneClick text-black flex gap-2 items-center justify-between py-4 px-[.75rem] no-underline hover:no-underline hover:bg-transparent hover:text-black" href={`tel:${phoneNumber}`} data-marchex>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="20" height="20" role="presentation">
                      <path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z" />
                    </svg>
                    <div className="w-full text-left">
                      {phoneNumber}
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="rotate-45" width="20" height="20" role="presentation">
                      <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2 160 448c0 17.7 14.3 32 32 32s32-14.3 32-32l0-306.7L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z" />
                    </svg>
                  </a>
                )}
              </Translation>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


SMFacilityInformation.propTypes = propTypes;
SMFacilityInformation.defaultProps = defaultProps;

export default SMFacilityInformation;
